/**
 * The facts a track's summary is built from, read the way TrackSummaryCard
 * reads them: the server's own readiness check, the result published for the
 * track, and how many of its sessions have ranked. One reader, so the summary
 * card and the track story (story overlay) can never disagree about a track.
 */
import { useEffect, useState } from 'react';
import { useTrackSessions, isSessionRetired } from '@/stores/prioritization/sessionStore';
import { evaluateTrackReadiness } from '@/lib/trackReadinessEvaluator';
import { fetchPublishedTrackResults, fetchCurrentTrackResult } from '@/lib/tracks/publishedTrackResult';
import type { TrackSummaryServerFacts, TrackSessionCounts } from './trackSummaryRows';

const NOT_READ: TrackSummaryServerFacts = { readiness: undefined, published: undefined, record: undefined };

/**
 * Readiness, the published result and (when an item is named) the newest
 * result record. Each answer arrives on its own; `undefined` is still reading,
 * `null` could not be read. A change of `refreshKey` reads them again (the
 * track page passes the locks and counts it holds, so a lock made on the page
 * shows at once). The facts already shown stay until the new answer arrives.
 */
export function useTrackSummaryServerFacts(trackId: string, itemId?: string, refreshKey?: string): TrackSummaryServerFacts {
  const [server, setServer] = useState<TrackSummaryServerFacts>(NOT_READ);
  useEffect(() => {
    if (!trackId) return;
    let live = true;
    const set = (patch: Partial<TrackSummaryServerFacts>) => live && setServer((s) => ({ ...s, ...patch }));
    evaluateTrackReadiness(trackId).then((readiness) => set({ readiness }), () => set({ readiness: null }));
    fetchPublishedTrackResults([trackId]).then((m) => set({ published: m.get(trackId) ?? null }), () => set({ published: null }));
    if (itemId) fetchCurrentTrackResult(trackId).then((record) => set({ record }), () => set({ record: null }));
    return () => {
      live = false;
    };
  }, [trackId, itemId, refreshKey]);
  // A different track starts from nothing read.
  useEffect(() => {
    setServer(NOT_READ);
  }, [trackId, itemId]);
  return server;
}

/** The track's live sessions, and how many of them have ranked (run complete or published). */
export function useTrackSessionCounts(trackId: string): TrackSessionCounts {
  const trackSessions = useTrackSessions(trackId).filter((s) => !isSessionRetired(s));
  return {
    total: trackSessions.length,
    ranked: trackSessions.filter((s) => s.state === 'RUN_COMPLETE' || s.state === 'PUBLISHED').length,
  };
}
